import React, { useCallback, useContext, useEffect, useState } from "react";
import liff from "@line/liff";
import dayjs from "dayjs";
import { MainStore } from "core/store";
import {
  Button,
  Card,
  Carousel,
  Divider,
  Empty,
  Image,
  Modal,
  Rate,
  Spin,
  Tag,
  message,
} from "antd";
import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import tipOfferApi from "api/tipOfferApi";
import caddyApi from "api/caddyApi";
import EmptyImage from "assets/images/png/empty-image.png";
import { GOLFER_ROUTES, buildBookingPaymentPath } from "constants/routesPath";
import {
  APPLICATION_STATUS,
  GENERIC_ERROR,
  OFFER_STATUS,
  apiErrorMessage,
  formatCurrency,
  formatOfferDate,
  formatOfferTime,
  formatPlayMode,
  formatRemaining,
  statusLabel,
  teeQuery,
} from "utils/tipOffer";

// jobs.status values used to decide what a matched offer can do next.
const BOOKING_AWAITING_PAYMENT = 1;
const BOOKING_CANCELLED = 3;

// Minimum tap-target size for accessibility (spec: >= 44px).
const TAP_TARGET = 44;

function bilingual({ th, en }) {
  return th === en ? th : `${th} (${en})`;
}

function applicantName(app) {
  return (
    app.display_name ||
    [app.first_name, app.last_name].filter(Boolean).join(" ") ||
    "-"
  );
}

/**
 * Countdown to the end of the selection window. Calls `onElapsed` once when the
 * window closes so the page can refresh the (now expired) offer.
 */
function SelectionCountdown({ expireAt, onElapsed }) {
  const [remaining, setRemaining] = useState(() => formatRemaining(expireAt));

  useEffect(() => {
    let fired = false;
    const timer = setInterval(() => {
      const next = formatRemaining(expireAt);
      setRemaining(next);
      if (!next && !fired) {
        fired = true;
        onElapsed();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [expireAt, onElapsed]);

  if (!remaining) {
    return <span>หมดเวลาเลือกแล้ว (Selection time is over)</span>;
  }
  return (
    <span>
      เหลือเวลาเลือกแคดดี้ {remaining} (Time left to choose: {remaining})
    </span>
  );
}

/**
 * What a matched offer's golfer can do next, based on the booking's status:
 * pay (awaiting payment), see that payment timed out, or open their bookings.
 */
function MatchedBookingCard({ offer, navigate }) {
  const awaitingPayment =
    Number(offer.booking_status) === BOOKING_AWAITING_PAYMENT &&
    !offer.booking_is_paid;

  if (awaitingPayment && offer.booking_caddy_id && offer.booking_id) {
    return (
      <Card style={{ marginBottom: 12 }}>
        <p style={{ marginTop: 0 }}>
          เลือกแคดดี้แล้ว กรุณาชำระเงินเพื่อยืนยันการจอง
          <br />A caddie was selected. Pay to confirm the booking.
        </p>
        <Button
          type="primary"
          block
          style={{ height: TAP_TARGET }}
          onClick={() =>
            navigate(
              buildBookingPaymentPath(
                offer.booking_caddy_id,
                offer.booking_id,
                teeQuery(offer)
              )
            )
          }
        >
          ชำระเงิน (Pay now)
        </Button>
      </Card>
    );
  }

  if (Number(offer.booking_status) === BOOKING_CANCELLED) {
    return (
      <Card style={{ marginBottom: 12 }}>
        <p style={{ margin: 0 }}>
          หมดเวลาชำระเงิน การจองถูกยกเลิกแล้ว
          <br />
          The payment time ran out and the booking was cancelled.
        </p>
      </Card>
    );
  }

  return (
    <Card style={{ marginBottom: 12 }}>
      <p style={{ marginTop: 0 }}>
        เลือกแคดดี้แล้ว ดูการจองได้ที่หน้ารายการจอง
        <br />A caddie was selected. See the booking in your bookings.
      </p>
      <Button
        type="primary"
        block
        style={{ height: TAP_TARGET }}
        onClick={() => navigate(GOLFER_ROUTES.BOOKINGS)}
      >
        ไปที่รายการจอง (Go to bookings)
      </Button>
    </Card>
  );
}

/**
 * "Book me a caddie" — golfer's offer detail (internal: tip-offer).
 *
 * Shows the offer and a countdown to the selection deadline, lists the caddies
 * who applied, and lets the golfer pick one. Picking a caddy creates the booking
 * and continues to that booking's payment page (/book_caddy/:caddy/:booking,
 * the same step as an accepted regular booking: summary, coupon, payment
 * countdown, Ksher). A matched offer whose booking is still unpaid can resume
 * payment from here. An open offer can also be cancelled. Also the target of
 * the "new applicant" LINE message.
 *
 * Requirements: 9.3, 9.4, 9.5, 9.9
 */
/**
 * Read-only caddie profile preview shown when a golfer taps an applicant.
 * Reuses the data shape returned by GET /caddy/search/:id (SearchProfile):
 * `file_uploads[].image_url` (profile + additional photos), `display_name`,
 * `caddy_number`, `golf_course.data.name`, `average_rate`, `review_count`,
 * `mode_preference`, `about_me`, and `reviews[]`. Deliberately omits the
 * booking controls of the full caddie screen so it stays a pure preview.
 * When the applicant is still selectable (open offer, status `applied`), the
 * footer also offers "Choose this caddie" so the golfer can pick from here.
 */
function CaddyProfileModal({ caddy, loading, error, canSelect, onSelect, onClose }) {
  const open = Boolean(caddy);
  const photos = Array.isArray(caddy?.file_uploads)
    ? caddy.file_uploads.filter((f) => f?.image_url)
    : [];
  // While loading (or on error) fall back to the summary the list already has.
  const fallback = caddy?.__fallback || {};
  const name =
    caddy?.display_name ||
    [caddy?.first_name, caddy?.last_name].filter(Boolean).join(" ") ||
    fallback.display_name ||
    [fallback.first_name, fallback.last_name].filter(Boolean).join(" ") ||
    "-";
  const caddyNumber = caddy?.caddy_number || fallback.caddy_number || "-";
  const courseName = caddy?.golf_course?.data?.name;
  const rate = Number(caddy?.average_rate) || 0;
  const reviewCount = Number(caddy?.review_count) || 0;
  const modeText =
    caddy?.mode_preference === "walking"
      ? "เดิน (Walking)"
      : caddy?.mode_preference === "carting"
      ? "รถกอล์ฟ (Cart)"
      : caddy?.mode_preference
      ? "เดิน / รถกอล์ฟ (Both)"
      : null;

  return (
    <Modal
      open={open}
      title="โปรไฟล์แคดดี้ (Caddie profile)"
      footer={[
        <Button key="close" onClick={onClose} style={{ height: TAP_TARGET }}>
          ปิด (Close)
        </Button>,
        canSelect && !error ? (
          <Button
            key="select"
            type="primary"
            onClick={onSelect}
            style={{ height: TAP_TARGET }}
            aria-label={`เลือก ${name} (Choose this caddie)`}
          >
            เลือกแคดดี้คนนี้ (Choose this caddie)
          </Button>
        ) : null,
      ]}
      onCancel={onClose}
    >
      {error ? (
        <p role="alert" style={{ color: "#cf1322" }}>
          {error.th}
          <br />
          {error.en}
        </p>
      ) : (
        <div>
          {photos.length > 0 ? (
            <Carousel autoplay autoplaySpeed={2500} style={{ marginBottom: 16 }}>
              {photos.map((file, index) => (
                <div key={`caddie-photo-${index}`}>
                  <div
                    style={{
                      height: 280,
                      backgroundImage: `url(${file.image_url})`,
                      backgroundPosition: "center center",
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                    }}
                  />
                </div>
              ))}
            </Carousel>
          ) : (
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <Image
                src={fallback.profile_url || EmptyImage}
                fallback={EmptyImage}
                width={160}
                height={160}
                preview={false}
                style={{ borderRadius: 12, objectFit: "cover" }}
                alt={name}
              />
            </div>
          )}

          <h2 style={{ marginBottom: 4 }}>{name}</h2>
          <div style={{ color: "#666" }}>
            หมายเลขแคดดี้ (Caddie no.): {caddyNumber}
          </div>
          {courseName && (
            <div style={{ color: "#007AFF", marginTop: 4 }}>{courseName}</div>
          )}
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}
          >
            <Rate allowHalf disabled value={rate} />
            <span style={{ color: "#666" }}>({reviewCount} รีวิว)</span>
          </div>
          {modeText && (
            <div style={{ marginTop: 6, color: "#555" }}>
              ใช้รถหรือเดิน (Mode): {modeText}
            </div>
          )}

          {caddy?.about_me && (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ marginBottom: 0 }}>เกี่ยวกับฉัน (About me)</h3>
              <Divider style={{ margin: "6px 0" }} />
              <div>{caddy.about_me}</div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <h3 style={{ marginBottom: 0 }}>รีวิวล่าสุด (Recent reviews)</h3>
            <Divider style={{ margin: "6px 0" }} />
            {Array.isArray(caddy?.reviews) && caddy.reviews.length > 0 ? (
              caddy.reviews.map((review, index) => (
                <div
                  key={`review-${index}`}
                  style={{
                    padding: "8px 0",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <b>{review.golfer_name || review.display_name || "ผู้เล่น"}</b>
                    <Rate
                      allowHalf
                      disabled
                      value={Number(review.rate) || 0}
                      style={{ fontSize: 12 }}
                    />
                  </div>
                  {review.comment && (
                    <div style={{ color: "#555", marginTop: 2 }}>
                      {review.comment}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="ยังไม่มีรีวิว (No reviews yet)"
              />
            )}
          </div>

          {loading && (
            <div style={{ textAlign: "center", marginTop: 12 }}>
              <Spin indicator={<LoadingOutlined spin />} />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default function TipOfferDetail() {
  const { id: offerId } = useParams();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const { userData } = useContext(MainStore) || {};

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [offer, setOffer] = useState(null);
  const [applicants, setApplicants] = useState([]);

  const [pendingSelect, setPendingSelect] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [working, setWorking] = useState(false);

  // Read-only caddie profile preview opened by tapping an applicant.
  const [profileCaddy, setProfileCaddy] = useState(null);
  const [profileApplicant, setProfileApplicant] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoadError(null);
    try {
      const [offerResponse, applicantsResponse] = await Promise.all([
        tipOfferApi.getOffer(offerId),
        tipOfferApi.getApplicants(offerId),
      ]);
      setOffer(offerResponse?.data?.data || null);
      setApplicants(applicantsResponse?.data?.data || []);
    } catch (error) {
      console.error("Failed to load tip offer:", error);
      setLoadError(
        apiErrorMessage(error, {
          th: "ไม่สามารถโหลดข้อเสนอได้ กรุณาลองใหม่อีกครั้ง",
          en: "Unable to load the offer. Please try again.",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleSelect = useCallback(async () => {
    if (!pendingSelect || working) return;
    setWorking(true);
    try {
      const response = await tipOfferApi.selectCaddy(offerId, {
        application_id: pendingSelect.id,
      });
      const booking = response?.data?.data || {};
      setPendingSelect(null);

      // Post a booking-status text into the LINE chat, like the caddie's
      // accept/reject flow. A failure here (e.g. opened outside LINE) must
      // not block the golfer from continuing to payment.
      try {
        await liff.sendMessages([
          {
            type: "text",
            text:
              "สถานะการจอง: เลือกแคดดี้แล้ว\n" +
              "ผู้จอง: " +
              (userData?.display_name || "ไม่ระบุ") +
              "\n" +
              "แคดดี้: " +
              applicantName(pendingSelect) +
              "\n" +
              "เบอร์: " +
              (pendingSelect.caddy_number || "-") +
              "\n" +
              "รหัสการจอง: " +
              (booking.booking_ref || "-") +
              "\n" +
              "วันที่ทีออฟ: " +
              dayjs(offer?.tf_date).format("DD/MM/YYYY") +
              "\n" +
              "เวลาทีออฟ: " +
              dayjs(offer?.tf_time).format("HH:mm"),
          },
        ]);
      } catch (sendError) {
        console.error("Failed to send LINE message:", sendError);
      }
      // Continue to the booking's payment page, exactly like an accepted regular
      // booking (coupon, payment countdown, Ksher hand-off).
      navigate(
        buildBookingPaymentPath(
          booking.caddy_id ?? pendingSelect.caddy_id,
          booking.booking_id,
          teeQuery(offer)
        )
      );
    } catch (error) {
      console.error("Failed to select caddy:", error);
      setPendingSelect(null);
      messageApi.error(bilingual(apiErrorMessage(error, GENERIC_ERROR)));
      fetchAll();
    } finally {
      setWorking(false);
    }
  }, [
    pendingSelect,
    working,
    offerId,
    offer,
    userData,
    navigate,
    messageApi,
    fetchAll,
  ]);

  const handleCancel = useCallback(async () => {
    if (working) return;
    setWorking(true);
    try {
      await tipOfferApi.cancelOffer(offerId);
      messageApi.success("ยกเลิกข้อเสนอแล้ว (Offer cancelled)");
    } catch (error) {
      console.error("Failed to cancel tip offer:", error);
      messageApi.error(bilingual(apiErrorMessage(error, GENERIC_ERROR)));
    } finally {
      setConfirmCancel(false);
      setWorking(false);
      fetchAll();
    }
  }, [working, offerId, messageApi, fetchAll]);

  // Open the read-only profile preview for a tapped applicant. Reuses the
  // standalone caddie endpoint (GET /caddy/search/:id), which returns the
  // profile + additional images, rating, mode preference, about-me and reviews
  // from just the caddy id — no date/tee-time context needed.
  const handleViewProfile = useCallback(async (app) => {
    const caddyId = app?.caddy_id;
    if (!caddyId) return;
    setProfileError(null);
    setProfileLoading(true);
    setProfileApplicant(app); // remember which applicant this profile is for
    setProfileCaddy({ caddy_id: caddyId, __fallback: app }); // open the modal immediately
    try {
      const response = await caddyApi.getCaddyById(caddyId);
      setProfileCaddy(response?.data?.data || { caddy_id: caddyId, __fallback: app });
    } catch (error) {
      console.error("Failed to load caddie profile:", error);
      setProfileError(
        apiErrorMessage(error, {
          th: "ไม่สามารถโหลดโปรไฟล์แคดดี้ได้",
          en: "Unable to load the caddie profile.",
        })
      );
    } finally {
      setProfileLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spin indicator={<LoadingOutlined spin />} />
        <div style={{ marginTop: 8 }}>กำลังโหลด... (Loading...)</div>
      </div>
    );
  }

  if (loadError || !offer) {
    const msg = loadError || GENERIC_ERROR;
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <p role="alert">
          {msg.th}
          <br />
          {msg.en}
        </p>
        <Button
          onClick={() => {
            setLoading(true);
            fetchAll();
          }}
          style={{ height: TAP_TARGET }}
        >
          ลองใหม่ (Retry)
        </Button>
      </div>
    );
  }

  const status = statusLabel(OFFER_STATUS, offer.status);
  const isOpen = offer.status === "open";

  return (
    <div style={{ padding: 16, maxWidth: 600, margin: "0 auto" }}>
      {contextHolder}
      <h2 style={{ marginBottom: 4 }}>จองแคดดี้ให้ฉัน</h2>
      <p style={{ marginTop: 0, color: "#666" }}>Book me a caddie</p>

      <Card style={{ marginBottom: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Tag color={status.color} style={{ fontSize: 14, padding: "4px 12px" }}>
            {status.th} ({status.en})
          </Tag>
          <Button
            icon={<ReloadOutlined />}
            aria-label="รีเฟรช (Refresh)"
            onClick={fetchAll}
            style={{ height: TAP_TARGET, width: TAP_TARGET }}
          />
        </div>
        <p style={{ margin: "12px 0 4px" }}>
          <b>สนามกอล์ฟ (Golf course):</b> {offer.golf_course_name || "-"}
        </p>
        <p style={{ margin: "4px 0" }}>
          <b>วันที่ (Date):</b> {formatOfferDate(offer.tf_date)}
        </p>
        <p style={{ margin: "4px 0" }}>
          <b>เวลาทีออฟ (Tee time):</b> {formatOfferTime(offer.tf_time)}
        </p>
        <p style={{ margin: "4px 0" }}>
          <b>รูปแบบการเล่น (Play mode):</b> {formatPlayMode(offer.play_mode)}
        </p>
        <p style={{ margin: "4px 0" }}>
          <b>ทิป (Tip):</b> {formatCurrency(offer.tip_amount)} บาท (THB)
        </p>
        {isOpen && (
          <p style={{ margin: "8px 0 0", color: "#d46b08" }} aria-live="polite">
            <SelectionCountdown
              expireAt={offer.selection_expire_at}
              onElapsed={fetchAll}
            />
          </p>
        )}
      </Card>

      {offer.status === "matched" && (
        <MatchedBookingCard offer={offer} navigate={navigate} />
      )}

      <h3>
        ผู้สมัคร (Applicants) · {applicants.length}
      </h3>
      {applicants.length === 0 ? (
        <Empty
          description={
            isOpen
              ? "ยังไม่มีแคดดี้สมัคร (No caddies have applied yet)"
              : "ไม่มีผู้สมัคร (No applicants)"
          }
        />
      ) : (
        applicants.map((app) => {
          const appStatus = statusLabel(APPLICATION_STATUS, app.status);
          const canSelect = isOpen && app.status === "applied";
          return (
            <Card
              key={app.id}
              style={{ marginBottom: 10 }}
              data-testid={`applicant-${app.id}`}
            >
              <div
                role="button"
                tabIndex={0}
                onClick={() => handleViewProfile(app)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleViewProfile(app);
                  }
                }}
                aria-label={`ดูโปรไฟล์ ${applicantName(app)} (View caddie profile)`}
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <Image
                  src={app.profile_url || EmptyImage}
                  fallback={EmptyImage}
                  width={64}
                  height={64}
                  preview={false}
                  style={{ borderRadius: 8, objectFit: "cover" }}
                  alt={applicantName(app)}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{applicantName(app)}</div>
                  <div style={{ color: "#666" }}>
                    หมายเลขแคดดี้ (Caddie no.): {app.caddy_number || "-"}
                  </div>
                  <div>
                    ทิปที่ยอมรับ (Accepted tip):{" "}
                    {formatCurrency(app.accepted_tip_amount)} บาท
                  </div>
                  <Tag color={appStatus.color} style={{ marginTop: 4 }}>
                    {appStatus.th} ({appStatus.en})
                  </Tag>
                  <div style={{ color: "#007AFF", fontSize: 13, marginTop: 4 }}>
                    ดูโปรไฟล์และรูปภาพ (View profile & photos)
                  </div>
                </div>
              </div>
              {canSelect && (
                <Button
                  type="primary"
                  block
                  style={{ height: TAP_TARGET, marginTop: 12 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingSelect(app);
                  }}
                  aria-label={`เลือก ${applicantName(app)} (Choose this caddie)`}
                >
                  เลือกแคดดี้คนนี้ (Choose this caddie)
                </Button>
              )}
            </Card>
          );
        })
      )}

      {isOpen && (
        <Button
          danger
          block
          style={{ height: TAP_TARGET, marginTop: 16 }}
          onClick={() => setConfirmCancel(true)}
        >
          ยกเลิกข้อเสนอ (Cancel offer)
        </Button>
      )}

      <CaddyProfileModal
        caddy={profileCaddy}
        loading={profileLoading}
        error={profileError}
        canSelect={
          isOpen && profileApplicant?.status === "applied"
        }
        onSelect={() => {
          const app = profileApplicant;
          setProfileCaddy(null);
          setProfileError(null);
          setProfileApplicant(null);
          if (app) setPendingSelect(app);
        }}
        onClose={() => {
          setProfileCaddy(null);
          setProfileError(null);
          setProfileApplicant(null);
        }}
      />

      <Modal
        open={Boolean(pendingSelect)}
        title="ยืนยันการเลือกแคดดี้ (Confirm caddie)"
        okText="ยืนยันและชำระเงิน (Confirm and pay)"
        cancelText="ยกเลิก (Back)"
        confirmLoading={working}
        onOk={handleSelect}
        onCancel={() => setPendingSelect(null)}
      >
        {pendingSelect && (
          <p>
            เลือก {applicantName(pendingSelect)} ด้วยทิป{" "}
            {formatCurrency(pendingSelect.accepted_tip_amount)} บาท
            ระบบจะพาไปหน้าชำระเงิน
            <br />
            Choose {applicantName(pendingSelect)} for a tip of{" "}
            {formatCurrency(pendingSelect.accepted_tip_amount)} THB. You will go
            to payment next.
          </p>
        )}
      </Modal>

      <Modal
        open={confirmCancel}
        title="ยกเลิกข้อเสนอ? (Cancel this offer?)"
        okText="ยกเลิกข้อเสนอ (Cancel offer)"
        okButtonProps={{ danger: true }}
        cancelText="ไม่ (Keep offer)"
        confirmLoading={working}
        onOk={handleCancel}
        onCancel={() => setConfirmCancel(false)}
      >
        <p>
          แคดดี้ที่สมัครไว้จะไม่ได้รับเลือก
          <br />
          Caddies who applied will not be selected.
        </p>
      </Modal>
    </div>
  );
}
